import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Check, ChevronDown, ChevronRight, Copy, Download, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import {
  leadFormsKey,
  metaPagesKey,
  useFormFields,
  useLeadForms,
  useMetaPages,
  useWebhookStatus,
  webhookStatusKey,
} from '../hooks/useMeta';
import { BRAND_LABELS, formatDateTime } from '../lib/format';
import type {
  BrandId,
  EventRouteDto,
  EventRouteMatch,
  EventRouteUpdate,
  LeadFormDto,
  LeadFormUpdate,
  MetaPageDto,
  WebhookStatusDto,
} from '@shared/types';

const LOCALES: Array<{ value: string; label: string }> = [
  { value: 'cs', label: 'Čeština' },
  { value: 'sk', label: 'Slovenština' },
  { value: 'en', label: 'Angličtina' },
  { value: 'de', label: 'Němčina' },
  { value: 'pl', label: 'Polština' },
  { value: 'hu', label: 'Maďarština' },
];

const FLAG_LABELS: Array<{ key: keyof WebhookStatusDto; label: string }> = [
  { key: 'appSecretSet', label: 'App Secret (podpis webhooku)' },
  { key: 'verifyTokenSet', label: 'Verify Token (ověření odběru)' },
  { key: 'globalTokenSet', label: 'System User token (Meta API)' },
  { key: 'crmTokenSet', label: 'CRM token' },
  { key: 'resendKeySet', label: 'Resend API klíč (e-maily)' },
  { key: 'whatchimpKeySet', label: 'Whatchimp API klíč (WhatsApp)' },
];

export default function PagesForms() {
  const { isAdmin } = useAuth();

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-dark">Stránky a formuláře</h1>
      <WebhookStatusCard />
      <PagesCard canEdit={isAdmin} />
      <FormsCard canEdit={isAdmin} />
      {!isAdmin && (
        <p className="text-xs text-gray-500">
          Změny nastavení stránek a formulářů může provádět jen admin.
        </p>
      )}
    </div>
  );
}

// --- (a) Stav webhooku ----------------------------------------------------

function WebhookStatusCard() {
  const { data, isLoading } = useWebhookStatus();
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);

  // Ruční dotažení leadů zpětně z Graph API (Meta je drží max 90 dní).
  const [backfillDeliver, setBackfillDeliver] = useState(false);
  const backfill = useMutation({
    mutationFn: () =>
      api.post<{ forms: number; leads: number }>('/meta/backfill', {
        sinceDays: 90,
        deliver: backfillDeliver,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  if (isLoading) return <div className="card p-8 text-sm text-gray-500">Načítám…</div>;
  if (!data) {
    return (
      <div className="card p-5 text-sm text-gray-500">Stav webhooku se nepodařilo načíst.</div>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.callbackUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="card space-y-4 p-5">
      <h2 className="font-heading text-lg font-semibold text-dark">Stav webhooku</h2>

      <div className="grid gap-2 sm:grid-cols-2">
        {FLAG_LABELS.map(({ key, label }) => (
          <div key={key} className="flex items-center gap-2 text-sm">
            <span
              className={`inline-block h-2.5 w-2.5 rounded-full ${
                data[key] ? 'bg-positive' : 'bg-danger'
              }`}
            />
            <span className="text-gray-700">{label}</span>
            <span className="ml-auto text-xs text-gray-500">
              {data[key] ? 'nastaveno' : 'chybí'}
            </span>
          </div>
        ))}
      </div>

      <label className="block">
        <span className="label">Callback URL pro Meta webhook</span>
        <div className="flex items-center gap-2">
          <input className="input font-mono text-xs" readOnly value={data.callbackUrl} />
          <button className="btn-secondary whitespace-nowrap" onClick={copy}>
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Zkopírováno' : 'Kopírovat'}
          </button>
        </div>
      </label>

      <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-700">
        {data.lastWebhook ? (
          <>
            <span className="font-medium">Poslední webhook:</span>{' '}
            {formatDateTime(data.lastWebhook.receivedAt)} · {data.lastWebhook.outcome} ·{' '}
            {data.lastWebhook.entries} položek
            {data.lastWebhook.detail ? ` · ${data.lastWebhook.detail}` : ''}
          </>
        ) : (
          'Zatím nedorazil žádný webhook.'
        )}
      </div>

      {data.rejected7d > 0 && (
        <div className="rounded-md border border-warning/40 bg-warning-bg p-3 text-sm text-warning">
          Za posledních 7 dní bylo odmítnuto {data.rejected7d} webhooků — zkontroluj App Secret a
          Verify Token.
        </div>
      )}

      {isAdmin && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <button
              className="btn-secondary"
              disabled={backfill.isPending}
              onClick={() => {
                const msg = backfillDeliver
                  ? 'Dotáhnout leady za posledních 90 dní a VŠECHNY nové rovnou odeslat do CRM (+ případné notifikace dle nastavení formulářů)?'
                  : 'Dotáhnout leady za posledních 90 dní jen do aplikace? Do CRM je pak pošleš ručně z detailu leadu.';
                if (window.confirm(msg)) backfill.mutate();
              }}
            >
              <RefreshCw size={16} className={backfill.isPending ? 'animate-spin' : ''} />
              {backfill.isPending ? 'Dotahuji…' : 'Dotáhnout leady zpětně (90 dní)'}
            </button>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={backfillDeliver}
                onChange={(e) => setBackfillDeliver(e.target.checked)}
              />
              rovnou odeslat do CRM
            </label>
          </div>
          <p className="text-xs text-gray-500">
            Bez zaškrtnutí se leady jen naimportují (stav „Přeskočeno — import") a do CRM je pošleš
            ručně z detailu. Se zaškrtnutím je do CRM postupně odešle cron (po dávkách ~20 à 15
            minut) vč. notifikací makléřům; potvrzovací e-mail se zpětně dotaženým leadům neposílá.
          </p>
          {backfill.isSuccess && (
            <span className="text-sm text-positive">
              Hotovo — {backfill.data.forms} formulářů, {backfill.data.leads} nových leadů.
            </span>
          )}
          {backfill.isError && (
            <span className="text-sm text-danger">Backfill selhal — zkus to znovu.</span>
          )}
        </div>
      )}
    </section>
  );
}

// --- (b) Stránky ----------------------------------------------------------

function PagesCard({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const { data: pages = [], isLoading } = useMetaPages();
  const [token, setToken] = useState('');

  const invalidatePages = () => {
    qc.invalidateQueries({ queryKey: metaPagesKey });
    qc.invalidateQueries({ queryKey: webhookStatusKey });
  };

  const importPages = useMutation({
    mutationFn: () => api.post<MetaPageDto[]>('/meta/pages/import', { token }),
    onSuccess: (list) => {
      qc.setQueryData(metaPagesKey, list);
      setToken('');
      invalidatePages();
    },
  });

  const savePage = useMutation({
    mutationFn: ({ id, brandId }: { id: string; brandId: BrandId | null }) =>
      api.put<MetaPageDto>(`/meta/pages/${id}`, { brandId }),
    onSuccess: invalidatePages,
  });

  const subscribe = useMutation({
    mutationFn: (id: string) => api.post<MetaPageDto>(`/meta/pages/${id}/subscribe`),
    onSuccess: invalidatePages,
  });

  const check = useMutation({
    mutationFn: (id: string) => api.get<MetaPageDto>(`/meta/pages/${id}/check`),
    onSuccess: invalidatePages,
  });

  const loadForms = useMutation({
    mutationFn: (id: string) => api.get<LeadFormDto[]>(`/meta/pages/${id}/forms`),
    onSuccess: () => qc.invalidateQueries({ queryKey: leadFormsKey }),
  });

  const busy =
    importPages.isPending ||
    savePage.isPending ||
    subscribe.isPending ||
    check.isPending ||
    loadForms.isPending;

  return (
    <section className="card space-y-4 p-5">
      <h2 className="font-heading text-lg font-semibold text-dark">Stránky</h2>

      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-[16rem] flex-1">
          <span className="label">System User token</span>
          <input
            type="password"
            className="input"
            placeholder="EAA…"
            autoComplete="off"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            disabled={!canEdit}
          />
          <span className="mt-1 block text-xs text-gray-500">
            Token se nikam neukládá — použije se jen pro tento import.
          </span>
        </label>
        <button
          className="btn-primary"
          disabled={!canEdit || !token.trim() || busy}
          onClick={() => importPages.mutate()}
        >
          <Download size={16} />
          {importPages.isPending ? 'Importuji…' : 'Importovat stránky'}
        </button>
      </div>

      {importPages.isError && <Banner kind="error" text={errorText(importPages.error)} />}
      {importPages.isSuccess && !importPages.isPending && (
        <Banner kind="success" text="Stránky byly naimportovány." />
      )}
      {(savePage.isError || subscribe.isError || check.isError || loadForms.isError) && (
        <Banner
          kind="error"
          text={errorText(savePage.error || subscribe.error || check.error || loadForms.error)}
        />
      )}

      {isLoading ? (
        <div className="text-sm text-gray-500">Načítám…</div>
      ) : pages.length === 0 ? (
        <p className="text-sm text-gray-500">
          Zatím žádné stránky. Naimportuj je pomocí System User tokenu výše.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Stránka</th>
                <th className="px-4 py-3 text-left font-semibold">ID</th>
                <th className="px-4 py-3 text-left font-semibold">Značka</th>
                <th className="px-4 py-3 text-left font-semibold">Odběr</th>
                <th className="px-4 py-3 text-right font-semibold">Akce</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((p) => (
                <tr key={p.id} className="border-t border-gray-100 align-middle">
                  <td className="px-4 py-3 font-medium">
                    {p.name}
                    {!p.hasToken && (
                      <span className="ml-2 badge bg-warning-bg text-warning">bez tokenu</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{p.id}</td>
                  <td className="px-4 py-3">
                    <BrandSelect
                      value={p.brandId}
                      disabled={!canEdit || busy}
                      onChange={(brandId) => savePage.mutate({ id: p.id, brandId })}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`badge ${
                        p.subscribed ? 'bg-positive-bg text-positive' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {p.subscribed ? 'Přihlášeno' : 'Nepřihlášeno'}
                    </span>
                    <div className="mt-1 text-xs text-gray-400">
                      {p.lastCheckedAt ? `Kontrola ${formatDateTime(p.lastCheckedAt)}` : 'Nekontrolováno'}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap justify-end gap-1">
                      {!p.subscribed && (
                        <button
                          className="btn-ghost text-xs"
                          disabled={!canEdit || busy}
                          onClick={() => subscribe.mutate(p.id)}
                        >
                          Přihlásit odběr
                        </button>
                      )}
                      <button
                        className="btn-ghost text-xs"
                        disabled={!canEdit || busy}
                        onClick={() => check.mutate(p.id)}
                      >
                        <RefreshCw size={14} /> Zkontrolovat
                      </button>
                      <button
                        className="btn-ghost text-xs"
                        disabled={!canEdit || busy}
                        onClick={() => loadForms.mutate(p.id)}
                      >
                        Načíst formuláře
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// --- (c) Formuláře --------------------------------------------------------

function FormsCard({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const { data: forms = [], isLoading } = useLeadForms();
  const { data: pages = [] } = useMetaPages();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Archivované formuláře v Metě už nesbírají — defaultně je schováváme.
  const archivedCount = forms.filter((f) => f.status.toUpperCase() === 'ARCHIVED').length;
  const visibleForms = showArchived
    ? forms
    : forms.filter((f) => f.status.toUpperCase() !== 'ARCHIVED');

  const save = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: LeadFormUpdate }) =>
      api.put<LeadFormDto>(`/meta/forms/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: leadFormsKey }),
  });

  const pageName = (pageId: string) => pages.find((p) => p.id === pageId)?.name ?? pageId;

  if (isLoading) return <div className="card p-8 text-sm text-gray-500">Načítám…</div>;

  return (
    <section className="card space-y-4 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-lg font-semibold text-dark">Formuláře</h2>
        {archivedCount > 0 && (
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Zobrazit archivované ({archivedCount})
          </label>
        )}
      </div>

      {save.isError && <Banner kind="error" text={errorText(save.error)} />}

      {visibleForms.length === 0 ? (
        <p className="text-sm text-gray-500">
          {forms.length === 0
            ? 'Zatím žádné formuláře. U stránky klikni na „Načíst formuláře".'
            : 'Všechny formuláře jsou archivované — zobraz je zaškrtávátkem vpravo nahoře.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
              <tr>
                <th className="px-3 py-3 text-left font-semibold">Formulář</th>
                <th className="px-3 py-3 text-left font-semibold">Stav</th>
                <th className="px-3 py-3 text-left font-semibold">Značka</th>
                <th className="px-3 py-3 text-center font-semibold">Aktivní</th>
                <th className="px-3 py-3 text-center font-semibold">CRM</th>
                <th className="px-3 py-3 text-center font-semibold">Events</th>
                <th className="px-3 py-3 text-center font-semibold">Potvrzení leadovi</th>
                <th className="px-3 py-3 text-center font-semibold">E-mail makléři</th>
                <th className="px-3 py-3 text-center font-semibold">WA makléři</th>
                <th className="px-3 py-3 text-center font-semibold">WA leadovi</th>
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {visibleForms.map((f) => (
                <FormRows
                  key={f.id}
                  form={f}
                  pageName={pageName(f.pageId)}
                  canEdit={canEdit}
                  busy={save.isPending}
                  expanded={expanded === f.id}
                  onToggleExpand={() => setExpanded(expanded === f.id ? null : f.id)}
                  onPatch={(patch) => save.mutate({ id: f.id, patch })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function FormRows({
  form,
  pageName,
  canEdit,
  busy,
  expanded,
  onToggleExpand,
  onPatch,
}: {
  form: LeadFormDto;
  pageName: string;
  canEdit: boolean;
  busy: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onPatch: (patch: LeadFormUpdate) => void;
}) {
  const toggles: Array<{ key: keyof LeadFormDto; value: boolean }> = [
    { key: 'active', value: form.active },
    { key: 'crmEnabled', value: form.crmEnabled },
    { key: 'eventsEnabled', value: form.eventsEnabled },
    { key: 'emailEnabled', value: form.emailEnabled },
    { key: 'brokerEmailEnabled', value: form.brokerEmailEnabled },
    { key: 'whatchimpBrokerEnabled', value: form.whatchimpBrokerEnabled },
    { key: 'whatchimpLeadEnabled', value: form.whatchimpLeadEnabled },
  ];

  return (
    <>
      <tr className="border-t border-gray-100">
        <td className="px-3 py-3">
          <div className="font-medium">{form.name}</div>
          <div className="text-xs text-gray-500">{pageName}</div>
        </td>
        <td className="px-3 py-3 text-xs text-gray-600">{form.status}</td>
        <td className="px-3 py-3">
          <BrandSelect
            value={form.brandId}
            disabled={!canEdit || busy}
            onChange={(brandId) => onPatch({ brandId })}
          />
        </td>
        {toggles.map((t) => (
          <td key={String(t.key)} className="px-3 py-3 text-center">
            <input
              type="checkbox"
              checked={t.value}
              disabled={!canEdit || busy}
              onChange={(e) => onPatch({ [t.key]: e.target.checked } as LeadFormUpdate)}
            />
          </td>
        ))}
        <td className="px-3 py-3 text-right">
          <button className="btn-ghost text-xs" onClick={onToggleExpand}>
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Detail
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-gray-100 bg-gray-50">
          <td colSpan={11} className="px-3 py-4">
            <FormDetail form={form} canEdit={canEdit} busy={busy} onPatch={onPatch} />
          </td>
        </tr>
      )}
    </>
  );
}

function FormDetail({
  form,
  canEdit,
  busy,
  onPatch,
}: {
  form: LeadFormDto;
  canEdit: boolean;
  busy: boolean;
  onPatch: (patch: LeadFormUpdate) => void;
}) {
  const [emails, setEmails] = useState(form.brokerEmails.join('\n'));
  const [phones, setPhones] = useState(form.brokerPhones.join('\n'));
  const [locale, setLocale] = useState(form.locale);
  const [notes, setNotes] = useState(form.notes);
  const [endpoint, setEndpoint] = useState(form.eventsEndpoint);
  // Token se z API nevrací — prázdné pole znamená „neměnit“. Teprve když do něj
  // uživatel sáhne, odešle se: s hodnotou se uloží nový, prázdné ho smaže.
  const [token, setToken] = useState('');
  const [tokenTouched, setTokenTouched] = useState(false);

  const dirty =
    emails !== form.brokerEmails.join('\n') ||
    phones !== form.brokerPhones.join('\n') ||
    locale !== form.locale ||
    notes !== form.notes ||
    endpoint !== form.eventsEndpoint ||
    tokenTouched;

  const submit = () => {
    onPatch({
      brokerEmails: splitLines(emails),
      brokerPhones: splitLines(phones),
      locale,
      notes,
      eventsEndpoint: endpoint.trim(),
      ...(tokenTouched ? { eventsToken: token.trim() } : {}),
    });
    setTokenTouched(false);
    setToken('');
  };

  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-3">
        <label className="block">
          <span className="label">E-maily makléřů (jeden na řádek)</span>
          <textarea
            className="input font-mono text-xs"
            rows={4}
            value={emails}
            disabled={!canEdit}
            onChange={(e) => setEmails(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="label">Telefony makléřů (jeden na řádek)</span>
          <textarea
            className="input font-mono text-xs"
            rows={4}
            value={phones}
            disabled={!canEdit}
            onChange={(e) => setPhones(e.target.value)}
          />
        </label>
        <div className="space-y-3">
          <label className="block">
            <span className="label">Jazyk komunikace</span>
            <select
              className="input"
              value={locale}
              disabled={!canEdit}
              onChange={(e) => setLocale(e.target.value)}
            >
              {LOCALES.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">Poznámka</span>
            <textarea
              className="input"
              rows={2}
              value={notes}
              disabled={!canEdit}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
        </div>
      </div>
      <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        <h4 className="font-heading text-sm font-semibold text-dark">
          Odeslání do aplikace pro eventy
        </h4>
        {form.eventsRoutes.length > 0 ? (
          <p className="text-sm text-gray-600">
            Formulář posílá leady na akce podle odpovědi — viz pravidla níže. Výchozí endpoint se
            nepoužívá.
          </p>
        ) : (
          <>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block">
              <span className="label">Endpoint akce</span>
              <input
                className="input font-mono text-xs"
                placeholder="https://events.buydubai.eu/api/events/<slug-akce>/registrations"
                value={endpoint}
                disabled={!canEdit}
                onChange={(e) => setEndpoint(e.target.value)}
              />
              <span className="mt-1 block text-xs text-gray-500">
                Najdeš ho v adminu akcí v sekci „Externí registrace (API)".
              </span>
            </label>
            <label className="block">
              <span className="label">API token akce</span>
              <input
                type="password"
                className="input font-mono text-xs"
                autoComplete="off"
                placeholder={form.hasEventsToken ? '••••••• (uložen)' : 'token z detailu akce'}
                value={token}
                disabled={!canEdit}
                onChange={(e) => {
                  setToken(e.target.value);
                  setTokenTouched(true);
                }}
              />
              <span className="mt-1 block text-xs text-gray-500">
                {form.hasEventsToken
                  ? 'Uložený token se nezobrazuje. Vyplněním ho přepíšeš, uložením prázdného pole smažeš.'
                  : 'Token se uloží zašifrovaně a už se nikdy nevypisuje.'}
              </span>
            </label>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.eventsSendConfirmation}
                disabled={!canEdit || busy}
                onChange={(e) => onPatch({ eventsSendConfirmation: e.target.checked })}
              />
              Nechat Events poslat potvrzení (e-mail, SMS)
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.eventsMarketingConsent}
                disabled={!canEdit || busy}
                onChange={(e) => onPatch({ eventsMarketingConsent: e.target.checked })}
              />
              Posílat marketingový souhlas
            </label>
          </div>
          {form.eventsEnabled && (!form.eventsEndpoint || !form.hasEventsToken) && (
            <Banner
              kind="error"
              text="Odesílání do Events je zapnuté, ale chybí endpoint nebo token — leady zůstanou nedoručené."
            />
          )}
          </>
        )}
        {/* key: po uložení server přidělí nová id → editor se znovu naplní z uloženého stavu. */}
        <EventRoutesEditor
          key={JSON.stringify(form.eventsRoutes)}
          form={form}
          canEdit={canEdit}
          busy={busy}
          onSave={(eventsRoutes) => onPatch({ eventsRoutes })}
        />
      </div>

      <div className="flex items-center gap-3">
        <button className="btn-primary" disabled={!canEdit || !dirty || busy} onClick={submit}>
          {busy ? 'Ukládám…' : 'Uložit detail formuláře'}
        </button>
        <span className="text-xs text-gray-500">
          Vlastní texty potvrzovacího e-mailu pro tento formulář (override šablony značky) nastavuje
          backend — pokud jsou prázdné, použije se šablona značky.
        </span>
      </div>
    </div>
  );
}

// --- pravidla akcí podle odpovědi -------------------------------------------

interface RouteDraft extends EventRouteDto {
  /** Nový token; prázdné + !tokenTouched = ponechat uložený. */
  token: string;
  tokenTouched: boolean;
}

const toDraft = (r: EventRouteDto): RouteDraft => ({ ...r, token: '', tokenTouched: false });

/**
 * Editor pravidel „když pole X obsahuje/je Y → pošli na akci Z“. Ukládá se celý seznam
 * najednou vlastním tlačítkem. Pole a hodnoty nabízí z posledních leadů formuláře.
 */
function EventRoutesEditor({
  form,
  canEdit,
  busy,
  onSave,
}: {
  form: LeadFormDto;
  canEdit: boolean;
  busy: boolean;
  onSave: (routes: EventRouteUpdate[]) => void;
}) {
  const [drafts, setDrafts] = useState<RouteDraft[]>(() => form.eventsRoutes.map(toDraft));
  const [dirty, setDirty] = useState(false);
  const { data: fieldData } = useFormFields(form.id);
  const fields = fieldData?.fields ?? [];

  const update = (i: number, patch: Partial<RouteDraft>) => {
    setDrafts((cur) => cur.map((d, j) => (j === i ? { ...d, ...patch } : d)));
    setDirty(true);
  };
  const move = (i: number, dir: -1 | 1) => {
    setDrafts((cur) => {
      const next = [...cur];
      const [d] = next.splice(i, 1);
      next.splice(i + dir, 0, d);
      return next;
    });
    setDirty(true);
  };
  const remove = (i: number) => {
    setDrafts((cur) => cur.filter((_, j) => j !== i));
    setDirty(true);
  };
  const add = () => {
    const prev = drafts[drafts.length - 1];
    setDrafts((cur) => [
      ...cur,
      {
        id: '',
        label: '',
        // Nové pravidlo nejspíš míří na stejnou otázku jako předchozí.
        field: prev?.field ?? '',
        match: 'contains',
        value: '',
        endpoint: '',
        hasToken: false,
        sendConfirmation: prev?.sendConfirmation ?? form.eventsSendConfirmation,
        marketingConsent: prev?.marketingConsent ?? form.eventsMarketingConsent,
        token: '',
        tokenTouched: false,
      },
    ]);
    setDirty(true);
  };

  const incomplete = drafts.some((d) => !d.field.trim() || !d.value.trim());
  const save = () => {
    onSave(
      drafts.map((d) => ({
        id: d.id,
        label: d.label.trim(),
        field: d.field.trim(),
        match: d.match,
        value: d.value.trim(),
        endpoint: d.endpoint.trim(),
        sendConfirmation: d.sendConfirmation,
        marketingConsent: d.marketingConsent,
        ...(d.tokenTouched ? { token: d.token.trim() } : {}),
      })),
    );
  };

  return (
    <div className="space-y-3 border-t border-gray-100 pt-3">
      <div>
        <h5 className="text-sm font-semibold text-dark">Rozdělení na akce podle odpovědi</h5>
        <p className="text-xs text-gray-500">
          Jeden formulář může plnit víc akcí. Pravidla se zkouší shora dolů a lead jde na první
          akci, jejíž pravidlo sedí. Lead, který nesedí na žádné, se do Events nepošle. Porovnání
          nerozlišuje velikost písmen ani diakritiku.
        </p>
      </div>

      {fields.length > 0 && (
        <datalist id={`fields-${form.id}`}>
          {fields.map((f) => (
            <option key={f.key} value={f.key} />
          ))}
        </datalist>
      )}

      {drafts.map((d, i) => {
        const values = fields.find((f) => f.key === d.field)?.values ?? [];
        const listId = `values-${form.id}-${i}`;
        return (
          <div key={d.id || `new-${i}`} className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500">{i + 1}.</span>
              <input
                className="input flex-1"
                placeholder="Název akce (např. Praha 12. 11.)"
                value={d.label}
                disabled={!canEdit}
                onChange={(e) => update(i, { label: e.target.value })}
              />
              <button
                className="btn-ghost !p-2"
                disabled={!canEdit || i === 0}
                onClick={() => move(i, -1)}
                aria-label="Posunout výš"
              >
                <ArrowUp size={14} />
              </button>
              <button
                className="btn-ghost !p-2"
                disabled={!canEdit || i === drafts.length - 1}
                onClick={() => move(i, 1)}
                aria-label="Posunout níž"
              >
                <ArrowDown size={14} />
              </button>
              <button
                className="btn-ghost !p-2 hover:!text-danger"
                disabled={!canEdit}
                onClick={() => remove(i)}
                aria-label="Smazat pravidlo"
              >
                <Trash2 size={14} />
              </button>
            </div>

            <div className="grid gap-3 md:grid-cols-[2fr_1fr_2fr]">
              <label className="block">
                <span className="label">Když pole</span>
                <input
                  className="input font-mono text-xs"
                  list={`fields-${form.id}`}
                  placeholder="klíč otázky z formuláře"
                  value={d.field}
                  disabled={!canEdit}
                  onChange={(e) => update(i, { field: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="label">Podmínka</span>
                <select
                  className="input"
                  value={d.match}
                  disabled={!canEdit}
                  onChange={(e) => update(i, { match: e.target.value as EventRouteMatch })}
                >
                  <option value="contains">obsahuje</option>
                  <option value="equals">je přesně</option>
                </select>
              </label>
              <label className="block">
                <span className="label">Hodnotu</span>
                <input
                  className="input"
                  list={values.length ? listId : undefined}
                  placeholder="odpověď, např. Praha"
                  value={d.value}
                  disabled={!canEdit}
                  onChange={(e) => update(i, { value: e.target.value })}
                />
                {values.length > 0 && (
                  <datalist id={listId}>
                    {values.map((v) => (
                      <option key={v.value} value={v.value}>
                        {`${v.count}×`}
                      </option>
                    ))}
                  </datalist>
                )}
              </label>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <label className="block">
                <span className="label">Endpoint akce</span>
                <input
                  className="input font-mono text-xs"
                  placeholder="https://events.buydubai.eu/api/events/<slug-akce>/registrations"
                  value={d.endpoint}
                  disabled={!canEdit}
                  onChange={(e) => update(i, { endpoint: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="label">API token akce</span>
                <input
                  type="password"
                  className="input font-mono text-xs"
                  autoComplete="off"
                  placeholder={d.hasToken ? '••••••• (uložen)' : 'token z detailu akce'}
                  value={d.token}
                  disabled={!canEdit}
                  onChange={(e) => update(i, { token: e.target.value, tokenTouched: true })}
                />
              </label>
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={d.sendConfirmation}
                  disabled={!canEdit}
                  onChange={(e) => update(i, { sendConfirmation: e.target.checked })}
                />
                Nechat Events poslat potvrzení
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={d.marketingConsent}
                  disabled={!canEdit}
                  onChange={(e) => update(i, { marketingConsent: e.target.checked })}
                />
                Posílat marketingový souhlas
              </label>
            </div>
            {!dirty && (!d.endpoint || !d.hasToken) && (
              <Banner
                kind="error"
                text="Pravidlu chybí endpoint nebo token — leady pro tuto akci zůstanou nedoručené."
              />
            )}
          </div>
        );
      })}

      <div className="flex flex-wrap items-center gap-3">
        <button className="btn-ghost" disabled={!canEdit || drafts.length >= 20} onClick={add}>
          <Plus size={14} /> Přidat pravidlo
        </button>
        {dirty && (
          <button className="btn-primary" disabled={!canEdit || busy || incomplete} onClick={save}>
            {busy ? 'Ukládám…' : 'Uložit pravidla'}
          </button>
        )}
        {dirty && incomplete && (
          <span className="text-xs text-danger">Každé pravidlo potřebuje pole i hodnotu.</span>
        )}
      </div>
    </div>
  );
}

// --- sdílené drobnosti ----------------------------------------------------

function BrandSelect({
  value,
  disabled,
  onChange,
}: {
  value: BrandId | null;
  disabled?: boolean;
  onChange: (brandId: BrandId | null) => void;
}) {
  return (
    <select
      className="input w-36"
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange((e.target.value || null) as BrandId | null)}
    >
      <option value="">— bez značky —</option>
      <option value="buydubai">{BRAND_LABELS.buydubai}</option>
      <option value="willroi">{BRAND_LABELS.willroi}</option>
    </select>
  );
}

function Banner({ kind, text }: { kind: 'error' | 'success'; text: string }) {
  return kind === 'error' ? (
    <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
      {text}
    </div>
  ) : (
    <div className="rounded-md border border-positive/40 bg-positive-bg p-3 text-sm text-positive">
      {text}
    </div>
  );
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : 'Akce selhala';
}

function splitLines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}
